import { AnimatePresence } from "framer-motion";
import {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { ModalProps } from "react-bootstrap";
import { createPortal } from "react-dom";
import { ReactComponent as Close } from "../../assets/svg/close.svg";
import useOutsideAlerter from "../../hooks/outsideAlerter/useOutsideAlerter";
import useKeydownHandler, {
  KeydownHandlerKeyEnum,
} from "../../hooks/useKeydownHandler";
import {
  CloseButton,
  Content,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalWrapper,
  Title,
} from "./style";

export interface IBaseModalProps extends ModalProps {
  style?: CSSProperties;
  title?: string | JSX.Element;
  titleStyle?: CSSProperties;
  hideCloseButton?: boolean;
  noContentPadding?: boolean;
  animateTitle?: boolean;
  showHeader?: boolean;
  isFullscreen?: boolean;
}

export function BaseModal({
  title,
  onHide,
  style,
  titleStyle,
  children,
  show,
  noContentPadding,
  animateTitle,
  hideCloseButton,
  showHeader = true,
  isFullscreen,
  ...others
}: PropsWithChildren<IBaseModalProps>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const clickOutside = useCallback(() => {
    if (Number(wrapperRef.current?.style.opacity) > 0) {
      onHide?.();
    }
  }, [onHide]);

  useOutsideAlerter([modalContentRef], clickOutside);
  useKeydownHandler(KeydownHandlerKeyEnum.ESC, clickOutside);

  /**
   * Focus on open, restored on close.
   *
   * `aria-modal="true"` is a claim that the rest of the page is inert. Making
   * that claim while leaving focus on whatever was behind the dialog is the
   * same kind of false promise as a combobox advertising a listbox it does not
   * render -- assistive tech is told one thing and finds another.
   *
   * Only takes focus if focus is not ALREADY inside the panel. CommandModal
   * puts `autoFocus` on its search input, and React applies that when the child
   * mounts -- which is before this parent effect runs. Focusing the panel
   * unconditionally would yank focus straight back out of the Cmd-K search box.
   *
   * Restore happens in the cleanup rather than on a close handler, because
   * AnimatePresence unmounts this subtree; a close-path-only restore would miss
   * every other way a modal can go away.
   */
  useEffect(() => {
    if (!show) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const panel = modalContentRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus();
    }
    return () => {
      // Only if the page has not already moved focus somewhere deliberate.
      if (!opener || !opener.isConnected) return;
      const active = document.activeElement;
      if (!active || active === document.body) opener.focus();
    };
  }, [show]);

  /**
   * Tab stays inside the dialog.
   *
   * A React onKeyDown rather than `useKeydownHandler`: that hook registers its
   * listener `{ passive: true }`, so it cannot preventDefault, and it passes no
   * event to the handler. Neither is usable for containing Tab.
   */
  const containTab = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== "Tab") return;
    const panel = modalContentRef.current;
    if (!panel) return;
    // No `offsetParent`/`getClientRects` visibility filter: both depend on
    // layout, which jsdom does not implement, so such a filter silently removes
    // EVERY candidate under test while looking correct in a browser. The panel's
    // contents are rendered only while it is open, and `:not([disabled])` plus
    // the tabindex exclusion already cover what a browser would skip.
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true");
    if (focusable.length === 0) {
      // Nothing to move to: keep focus on the panel rather than letting Tab
      // walk out into a page that is meant to be inert.
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const root = document.getElementById("modal-portal");

  if (root) {
    return createPortal(
      <AnimatePresence>
        {show && (
          <ModalWrapper
            key={"modalWrapper"}
            ref={wrapperRef}
            {...others}
          >
            {/* The dialog is the PANEL, not the backdrop. `{...others}` above
                spreads onto ModalWrapper, so a caller-passed role would have
                landed on the backdrop -- which is why this belongs here, in the
                one component every modal in the app already goes through.

                `title` is the modal's visible heading; when there is one it
                names the dialog, otherwise fall back to a generic name so the
                dialog is never announced anonymously. Escape-to-close and
                click-outside already work (useKeydownHandler /
                useOutsideAlerter above); this adds the semantics, not the
                behaviour. Focus trapping is deliberately NOT attempted here --
                it is a behavioural change across every modal and belongs in its
                own commit with its own tests. */}
            <ModalBody
              role="dialog"
              aria-modal="true"
              // -1: focusable programmatically, never a tab stop of its own.
              tabIndex={-1}
              onKeyDown={containTab}
              aria-label={typeof title === "string" && title ? title : "Dialog"}
              style={style}
              ref={modalContentRef}
              $isFullscreen={isFullscreen}
              transition={{
                duration: 0.1,
                ease: "easeInOut",
              }}
              initial={{
                scale: 0.9,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0.9,
                opacity: 0,
              }}
            >
              {/* CONTENT */}
              <ModalContent
                $isFullscreen={isFullscreen}
                transition={{
                  duration: 0.1,
                  ease: "easeInOut",
                }}
                exit={{
                  scale: 0.9,
                  opacity: 0,
                }}
              >
                {showHeader && (title || !hideCloseButton) && (
                  <ModalHeader>
                    <AnimatePresence>
                      <Title
                        style={titleStyle ? { ...titleStyle } : {}}
                        key={
                          animateTitle && typeof title === "string"
                            ? title
                            : "title"
                        }
                        transition={{
                          duration: 0.1,
                          ease: "easeInOut",
                        }}
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                      >
                        {title || ""}
                      </Title>
                    </AnimatePresence>
                    {/* Every modal in the app shares this button, so the
                        missing name was missing everywhere at once. */}
                    {onHide && !hideCloseButton && (
                      <CloseButton onClick={onHide} aria-label="Close">
                        <Close />
                      </CloseButton>
                    )}
                  </ModalHeader>
                )}
                <Content
                  isFullscreen={isFullscreen}
                  noPadding={noContentPadding}
                >
                  {children}
                </Content>
              </ModalContent>
            </ModalBody>
          </ModalWrapper>
        )}
      </AnimatePresence>,
      root
    );
  }
  return null;
}
